/**
 * 埋め込み視聴モーダルのフォント定義（タイル型・スライダー型・単独表示 共通モジュール）。
 *
 * 管理画面（プレイリスト詳細）で選択し、scenario.config.embedFont に
 * プレイリスト単位で保存する。埋め込み側（VideoModal → PCVideoModal / SPVideoModal）と
 * 管理画面プレビュー（EmbeddedVideoPreviewModal）の両方で同じ値を参照する。
 *
 * ## 適用先がモーダルだけである理由
 * タイル型・スライダー型のグリッド自体はサムネイル画像しか描画しておらず、
 * 文字が出るのはクリック後に開く視聴モーダル（タイトル・説明・出演者・CTA）だけ。
 *
 * ## 「先方サイトのフォントを継承」を選択肢に入れない理由
 * Shadow DOM は CSS 変数（--default-font-family 等）を継承するため、かつて親ページの
 * font-family がモーダル内に漏れて見た目がサイト毎に変わる事象があり、
 * PCVideoModal / SPVideoModal で明示的に固定して塞いだ経緯がある。
 * ここで inherit を選べるようにするとその修正を取り消すことになるので入れない。
 * 選べるのは moovy が用意したフォントのうちどれか、だけ。
 */

export const EMBED_FONTS = ['GOTHIC', 'SERIF', 'SYSTEM'] as const

export type EmbedFont = (typeof EMBED_FONTS)[number]

/** 未設定（旧データ）の既定値。従来の見た目を保つためゴシック（Noto Sans JP）。 */
export const DEFAULT_EMBED_FONT: EmbedFont = 'GOTHIC'

export const EMBED_FONT_LABELS: Record<EmbedFont, string> = {
  GOTHIC: 'ゴシック体',
  SERIF: '明朝体',
  SYSTEM: 'OS標準',
}

export const EMBED_FONT_DESCRIPTIONS: Record<EmbedFont, string> = {
  GOTHIC: '既定。読みやすく、どのサイトにも合わせやすい',
  SERIF: '落ち着いた印象。コーポレートサイト向け',
  SYSTEM: '閲覧者の端末の標準フォント。追加の読み込みが無く最も軽い',
}

/**
 * 実際に font-family へ流し込む値。
 * Web フォントが未到着／未対応の環境でも崩れないよう、OS 標準フォントまで必ず繋ぐ。
 */
const EMBED_FONT_STACKS: Record<EmbedFont, string> = {
  GOTHIC:
    '"Noto Sans JP", -apple-system, BlinkMacSystemFont, "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif',
  SERIF:
    '"Noto Serif JP", "Hiragino Mincho ProN", "Yu Mincho", YuMincho, "MS PMincho", serif',
  SYSTEM:
    'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", "Hiragino Kaku Gothic ProN", "Yu Gothic", sans-serif',
}

/**
 * 追加で読み込む必要がある Web フォントの stylesheet。
 *
 * GOTHIC（Noto Sans JP）は embeddedRender が mount 時点で既に注入しているため null。
 * SYSTEM は端末のフォントを使うので読み込み自体が不要（= 先方サイトへのリクエストが1本減る）。
 * SERIF を選んだときだけ追加のリクエストが発生する。
 */
const EMBED_FONT_HREFS: Record<EmbedFont, string | null> = {
  GOTHIC: null,
  SERIF: 'https://fonts.googleapis.com/css2?family=Noto+Serif+JP:wght@400;500;700&display=swap',
  SYSTEM: null,
}

/**
 * 不正な値（旧データや手動編集された JSON）から安全な既定値へフォールバックする。
 */
export function normalizeEmbedFont(value: unknown): EmbedFont {
  return typeof value === 'string' && (EMBED_FONTS as readonly string[]).includes(value)
    ? (value as EmbedFont)
    : DEFAULT_EMBED_FONT
}

/** インライン style / <style> タグ用の font-family 値を返す。 */
export function embedFontStack(font: EmbedFont): string {
  return EMBED_FONT_STACKS[font]
}

/**
 * 選択されたフォントの stylesheet を document.head へ一度だけ注入する。
 *
 * embeddedRender が mount 時に注入する Noto Sans JP と違い、こちらは
 * scenario.config が到着してからでないと必要なフォントが分からないため、
 * モーダル側から呼ぶ。id で冪等性を担保しており、複数の埋め込みが同居しても1本しか出ない。
 */
export function ensureEmbedFontLoaded(font: EmbedFont): void {
  const href = EMBED_FONT_HREFS[font]
  /* v8 ignore next 3 */
  if (!href || typeof document === 'undefined') {
    return
  }

  const id = `gai-agents-font-${font.toLowerCase()}`
  if (document.getElementById(id)) {
    return
  }

  const link = document.createElement('link')
  link.id = id
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}
